import 'react-toastify/dist/ReactToastify.css'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { zIndex } from '@mathflat/common/style'
import colors from '@mathflat/common/style/colors'
import clsx from 'clsx'
import type { ToastOptions } from 'react-toastify'
import { toast as _toast, ToastContainer as _ToastContainer } from 'react-toastify'

import { Icon } from '../Icon'
import type { IconType } from '../Icon/Icon'

// 1. toastFactory
type FactoryArg = {
  type: Extract<ToastOptions['type'], 'default' | 'error'>
  icon: {
    name: Extract<
      IconType,
      'check_circle' | 'alert_error_fill' | 'info_fill' | 'delete' | 'close_circle'
    >
    color: string
  }
  actionButton?: React.ReactNode
  opt?: ToastOptions
}

const toastFactory = ({ opt, ...props }: FactoryArg) => {
  const toastify = props.type === 'error' ? _toast.error : _toast // 'default' | 'error' 구분해서 띄움 -> 구분된 type값으로 className줄 수 있음 -> 구분해서 스타일링
  return (msg: ToastProps['message'], actionButton?: FactoryArg['actionButton']) =>
    toastify(<Toast {...props} message={msg} actionButton={actionButton} />, opt)
}

// 1-2. toastFactory case별 args (storybook을 위해 따로 빼놓음)
export const successFactoryArgs: FactoryArg = {
  type: 'default',
  icon: { name: 'check_circle', color: colors.green.$100 },
}

export const infoFactoryArgs: FactoryArg = {
  type: 'default',
  icon: { name: 'info_fill', color: colors.green.$100 },
}

export const failureFactoryArgs: FactoryArg = {
  type: 'default',
  icon: { name: 'close_circle', color: colors.red.$400 },
}

export const deleteFactoryArgs: FactoryArg = {
  type: 'default',
  icon: { name: 'delete', color: colors.red.$400 },
}

export const errorFactoryArgs: FactoryArg = {
  type: 'error',
  icon: { name: 'alert_error_fill', color: '#fff' },
}

export const errorRedFactoryArgs: FactoryArg = {
  type: 'default',
  icon: { name: 'alert_error_fill', color: colors.red.$400 },
}

// 2. ** 실제 이용하는 toast 띄우는 함수 **
export const toast = {
  success: toastFactory(successFactoryArgs),
  info: toastFactory(infoFactoryArgs),
  failure: toastFactory(failureFactoryArgs),
  delete: toastFactory(deleteFactoryArgs),
  error: toastFactory(errorFactoryArgs),
  errorRed: toastFactory(errorRedFactoryArgs),
}

// 3. 컴포넌트 - 토스트 컨테이너
// 3-1-(가). 토스트 컨테이너 스타일 (storybook을 위해 따로 빼놓음)
export const toastContainerStyle = css`
  --toastify-toast-width: auto;
  z-index: ${zIndex.FadeToast};

  padding: 0; //override .Toastify__toast-container
  .mathflat-toast-container {
    display: flex;
    align-items: center;
    width: fit-content;
    padding: 18px;
    border-radius: var(--Radius-100);
    border: 1px solid ${colors.gray.$400};
    box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.1);
    gap: 16px;

    + .mathflat-toast-container {
      margin-top: 10px;
    }

    &.default {
      background-color: #ffffff;
      color: #333333;
    }

    &.error {
      background-color: ${colors.gray.$700};
      color: #ffffff;

      .mathflat-toast-link {
        color: #fff;
      }
      .mathflat-toast-close-button svg path {
        color: #fff;
      }
    }
  }

  .mathflat-toast-body {
    padding: 0;
    > div {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 0; //override .Toastify__toast-body

      .mathflat-toast-message {
        display: flex;
        align-items: center;
        width: 250px;
        white-space: pre-wrap;
      }
    }
  }
`

const StyledToastContainer = styled(_ToastContainer)`
  ${toastContainerStyle}
`

// 3-1-(나). 토스트 컨테이너
export const ToastContainer = () => (
  <StyledToastContainer
    autoClose={3000}
    hideProgressBar
    progressStyle={{ visibility: 'hidden' }}
    closeButton={<ToastCloseButton />}
    position="bottom-left"
    bodyClassName="mathflat-toast-body"
    toastClassName={(context) => clsx('mathflat-toast-container', context?.type && context.type)} // default, error 클래스 추가
    draggablePercent={60}
    icon={false}
  />
)

// 3-2. 토스트 컴포넌트
export type ToastProps = Omit<FactoryArg, 'opt'> & {
  message: string
}

export const Toast = ({ icon, actionButton, message }: ToastProps) => {
  return (
    <>
      <Icon className="mathflat-toast-icon" name={icon.name} size={20} color={icon.color} />
      <p className="mathflat-toast-message">{message}</p>
      {/* TODO: 버튼 컴포넌트 생기면 교체해야 함 */}
      {actionButton ? actionButton : <></>}
    </>
  )
}

// 3-3. 토스트 닫기 버튼
export const ToastCloseButton = ({ closeToast }: any) => {
  return (
    <button onClick={closeToast} className="mathflat-toast-close-button">
      <Icon name="close" size={20} color={colors.gray.$700} />
    </button>
  )
}
